import { FC, useEffect, useState } from 'react';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoffee, faPencil, faUtensils } from '@fortawesome/free-solid-svg-icons';
import { formatPriceCurrency, getCurrency } from 'utils/utils';
import { DynamicProposal, HotProposal } from 'ts/shared/interfaces/CreateProposalParams';
import { dateFormat, formatPriceProposal } from 'utils/shared/utils';
import { faAccessibleIcon } from '@fortawesome/free-brands-svg-icons';
import RoomsFilter from 'components/hotel/Details/RoomsFilter';
import { getOriginalHotelKey } from 'utils/hotel';
import { publishLocalRoomFilterAction } from 'utils/assistantChatStore';
import { useProposalUi } from './Assistant/proposalActions';

interface ProposalNewRoomsProps {
  rooms: any[];
  choosenRoomIds: string[];
  setChoosenRoomIds: (ids: string) => void;
  hotel: any;
  onChangePrice: (hotel: any) => void;
  dynamic?: DynamicProposal;
  openPopup: (product: string, index: number, rcUniqueKey?: string) => void;
  hotProposal: HotProposal;
  hotelIndex: number;
  assistantOwner?: boolean;
  showAllRooms?: boolean;
  onShowAllRoomsChange?: (showAll: boolean) => void;
  totalRoomsCount?: number;
}

const ProposalNewRooms: FC<ProposalNewRoomsProps> = (props) => {
  const [rooms, setRooms] = useState(props.rooms);
  const [, setFilterRoom] = useState<any>();
  const currency = sessionStorage.getItem('displayCurrencyHotels') || 'usd';

  useProposalUi(
    (intent) =>
      !!props.assistantOwner &&
      intent.kind === 'filter-rooms' &&
      intent.hotelIndex === props.hotelIndex &&
      intent.dynamic === !!props.dynamic,
    (intent) => {
      if (intent.kind !== 'filter-rooms') {
        return;
      }
      const hotelKey = props.hotel?.htuKey
        ? getOriginalHotelKey(props.hotel.htuKey)
        : '';
      if (!hotelKey) {
        return;
      }
      window.setTimeout(() => {
        publishLocalRoomFilterAction({
          hotelKey,
          roomFilterName: intent.roomFilterName,
          roomRefundableOnly: intent.roomRefundableOnly,
          roomAccessibleOnly: intent.roomAccessibleOnly,
          roomBoardingOnly: intent.roomBoardingOnly,
          roomMealPlans: intent.roomMealPlans,
          roomSortByCxl: intent.roomSortByCxl,
          clearRoomFilters: intent.clearRoomFilters,
        });
      }, 50);
    },
  );

  useEffect(() => {
    const newRooms = props.rooms.map((room) => {
      const newHotelRooms = (room.hotelRooms || []).map((hotelRoom: any) => {
        const displayName = `${room.desc ? room.desc + ' - ' : ''}${hotelRoom?.desc ? hotelRoom.desc : ''
          } ${room.board?.basis?.desc ? room.board.basis.desc : ''}`;

        return {
          ...hotelRoom,
          displayName,
        };
      });

      const suppText = room.suppDesc
        ? room.suppDesc
        : props.hotel.item.sysSupp
          ? props.hotel.item.sysSupp
          : '';

      return {
        ...room,
        suppText,
        hotelRooms: newHotelRooms,
        displayDate: room.checkIn,
        displayPrice: room.price.value.toString(),
        visible: true,
        visibleSm: true,
      };
    });

    setRooms(newRooms);
  }, [props.rooms]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleRooms = rooms.filter((room) => room.visible && room.visibleSm);
  const selectedRooms = visibleRooms.filter((room) =>
    props.choosenRoomIds?.includes(room.rcUniqueKey),
  );
  const unselectedRooms = visibleRooms.filter(
    (room) => !props.choosenRoomIds?.includes(room.rcUniqueKey),
  );

  const renderRoom = (room: any, index: number) => (
    <div
      className={
        'hotel-card-room ' +
        (props.choosenRoomIds?.includes(room.rcUniqueKey) ? ' hotel-card-room-highlighted' : '') +
        (new Date(room.cxl.limitDate) < new Date() ? ' room-cxl' : '')
      }
      key={room.rcUniqueKey || index}
    >
      <div className="flex-one flex-row">
        <Checkbox
          label={''}
          checked={props.choosenRoomIds?.includes(room.rcUniqueKey)}
          onChange={() => props.setChoosenRoomIds(room.rcUniqueKey)}
        />
        <div className="text-lh cur-poi" onClick={() => props.setChoosenRoomIds(room.rcUniqueKey)}>
          {room.hotelRooms.map((hotelRoom: any) => (
            <div key={hotelRoom.rcUniqueKey}>{hotelRoom.displayName}</div>
          ))}
        </div>
      </div>

      <div className="room-icon active-ada" style={{ flex: `0 0 34px` }}>
        {!!room.hotelRooms[0].ada && (
          <div className="active-ada-i active-i">
            <FontAwesomeIcon icon={faAccessibleIcon} />
          </div>
        )}
      </div>
      {room.board?.basis?.code && room.board.basis.code !== 'RO' && (
        <div className="active-meal-i active-i">
          <FontAwesomeIcon icon={faUtensils} className="mr-5" /> {room.board.basis.code}
        </div>
      )}

      {room.board?.basis?.code === 'RO' && (
        <div className="active-meal-i active-i active-meal-i-ro">
          <FontAwesomeIcon icon={faCoffee} className="mr-5" /> RO
        </div>
      )}

      <div style={{ width: 3 }}></div>
      {new Date(room.cxl.limitDate) < new Date() ? (
        <div className="room-date room-date-danger">NO REF</div>
      ) : (
        <div dir="ltr" className="room-date">
          {dateFormat(room.cxl.limitDate, 'dd MMM', 'en')}
        </div>
      )}
      {!props.dynamic && (
        <span
          className="room-price-edit ml-10 cur-poi"
          onClick={() => props.openPopup('hotels', props.hotelIndex, room.rcUniqueKey)}
        >
          {!!room.viewValue && (
            <span className="text-contrast">
              {formatPriceCurrency(room.price[currency as keyof any], getCurrency(currency))} /{' '}
            </span>
          )}
          <span className="pr-data-table-title text-bold text-lg text-contrast text-uppercase">
            {formatPriceProposal(
              room.price[currency as keyof any],
              currency,
              room.viewType,
              room.viewValue,
            )}
            <FontAwesomeIcon icon={faPencil} className="ml-5" />
          </span>
        </span>
      )}
    </div>
  );

  return (
    <div className="hotel-card-rooms details-rooms pr-new-rooms">
      <div className="pr-new-rooms-sticky">
        <RoomsFilter
          rooms={rooms}
          setRooms={setRooms}
          setFilterRoom={setFilterRoom}
          hotel={props.hotel}
          showAllRooms={props.showAllRooms}
          onShowAllRoomsChange={props.onShowAllRoomsChange}
          totalRoomsCount={props.totalRoomsCount}
        />
        {selectedRooms.map(renderRoom)}
      </div>
      {unselectedRooms.map(renderRoom)}
    </div>
  );
};

export default ProposalNewRooms;
